import { useEffect, useState } from "react";
import { HERO_SLIDES } from "@/data/content";
import { ArrowRight } from "lucide-react";
import { Link } from "react-router-dom";
import { useT, useRoute } from "@/i18n";

function Countdown({ target, labels }) {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, []);
  const diff = new Date(target).getTime() - now;
  if (diff <= 0) return null;
  const units = [
    [Math.floor(diff / 86400000), labels.days],
    [Math.floor(diff / 3600000) % 24, labels.hours],
    [Math.floor(diff / 60000) % 60, labels.minutes],
    [Math.floor(diff / 1000) % 60, labels.seconds],
  ];
  return (
    <div data-testid="hero-countdown" className="mt-7 flex items-center gap-3 sm:gap-4">
      {units.map(([value, label]) => (
        <div
          key={label}
          className="bg-white/10 backdrop-blur-md border border-white/15 px-3 py-2.5 sm:px-4 sm:py-3 text-center min-w-[64px] sm:min-w-[76px]"
        >
          <p className="text-xl sm:text-2xl font-bold text-white tabular-nums leading-none">
            {String(value).padStart(2, "0")}
          </p>
          <p className="mt-1.5 text-[9px] uppercase tracking-[0.25em] text-white/60 font-bold">
            {label}
          </p>
        </div>
      ))}
    </div>
  );
}

export default function Hero() {
  const t = useT();
  const route = useRoute();
  const [index, setIndex] = useState(0);

  useEffect(() => {
    const id = setInterval(() => setIndex((i) => (i + 1) % HERO_SLIDES.length), 6500);
    return () => clearInterval(id);
  }, []);

  const slides = HERO_SLIDES.map((s, i) => ({
    ...s,
    eyebrow: t.hero[i]?.eyebrow || s.eyebrow,
    title: t.hero[i]?.title || s.title,
    cta: t.hero[i]?.cta || s.cta,
  }));

  return (
    <section
      data-testid="hero-section"
      className="relative w-full h-[calc(100vh-80px)] min-h-[600px] overflow-hidden bg-black"
    >
      {slides.map((s, i) => (
        <div
          key={i}
          className={`absolute inset-0 transition-opacity duration-[1600ms] ease-out ${
            i === index ? "opacity-100" : "opacity-0"
          }`}
        >
          <picture>
            {s.imageMobile && <source media="(max-width: 767px)" srcSet={s.imageMobile} />}
            <img
              src={s.image}
              alt={s.title}
              className={`absolute inset-0 w-full h-full object-cover transition-transform duration-[8000ms] ease-out ${
                i === index ? "scale-110" : "scale-100"
              }`}
            />
          </picture>
          <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/30 to-black/80" />
        </div>
      ))}

      {/* Content */}
      <div className="relative h-full max-w-[1600px] mx-auto px-6 md:px-10 lg:px-14 flex flex-col justify-end pb-24 md:pb-32">
        <div className="max-w-3xl">
          <p className="text-[10px] sm:text-[11px] uppercase tracking-[0.4em] text-[#C20E1A] font-bold mb-5">
            {slides[index].eyebrow}
          </p>
          <h1 className="heading-display text-white text-3xl sm:text-4xl lg:text-5xl xl:text-6xl uppercase max-w-2xl">
            {slides[index].title}
          </h1>
          {slides[index].countdownTo && (
            <Countdown target={slides[index].countdownTo} labels={t.countdown} />
          )}
          <div className="mt-8 flex flex-wrap items-center gap-4">
            {slides[index].link ? (
              <a
                href={slides[index].link}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="hero-explore-btn"
                className="bg-[#C20E1A] text-white px-7 py-3.5 text-[10px] uppercase tracking-[0.25em] font-bold hover:bg-white hover:text-black transition-colors duration-500 inline-flex items-center gap-3"
              >
                {slides[index].cta || t.common.explore} <ArrowRight size={12} />
              </a>
            ) : (
              <Link
                to={route("exhibitions")}
                data-testid="hero-explore-btn"
                className="bg-[#C20E1A] text-white px-7 py-3.5 text-[10px] uppercase tracking-[0.25em] font-bold hover:bg-white hover:text-black transition-colors duration-500 inline-flex items-center gap-3"
              >
                {t.common.explore} <ArrowRight size={12} />
              </Link>
            )}
            <Link
              to={route("about")}
              data-testid="hero-about-btn"
              className="border border-white/30 text-white px-7 py-3.5 text-[10px] uppercase tracking-[0.25em] font-bold hover:bg-white hover:text-black transition-colors duration-500"
            >
              {t.common.aboutArtful}
            </Link>
          </div>
        </div>
      </div>

      {/* Slide indicators */}
      <div className="absolute bottom-10 right-6 md:right-12 flex items-center gap-4">
        {slides.map((_, i) => (
          <button
            key={i}
            data-testid={`hero-dot-${i}`}
            onClick={() => setIndex(i)}
            className={`h-[2px] transition-all duration-500 ${
              i === index ? "w-12 bg-white" : "w-6 bg-white/30 hover:bg-white/60"
            }`}
            aria-label={`Show slide ${i + 1}`}
          />
        ))}
      </div>

      {/* Vertical caption */}
      <div className="hidden lg:block absolute left-6 bottom-24 text-white/40">
        <span className="vertical-rl text-[10px] uppercase tracking-[0.4em]">
          {t.common.scrollToDiscover}
        </span>
      </div>
    </section>
  );
}
